/**
 * LinkedInDripCard — W48 Marketing Dashboard Component (D)
 *
 * Active LinkedIn drip campaigns pulled from marketing_events WHERE
 * type ILIKE '%linkedin%'. Displays reach/engagement counters where available
 * in event description.
 *
 * Falls back gracefully to em-dash where data is unavailable.
 * No Recharts. Custom SVG icons + Tailwind.
 * Canon: teal #0F766E for LinkedIn accent.
 */

import { useEffect, useState } from "react";
import { getSession } from "@/lib/session";

interface LinkedInCampaign {
  id: string;
  name: string;
  event_date: string | null;
  status: string;
  campaign_theme: string | null;
  description: string | null;
  // reach/engagement parsed from description if present
  reach?: number | null;
  engagement?: number | null;
}

interface CalendarData {
  events: Array<{
    id: string;
    name: string;
    event_date: string | null;
    description: string | null;
    type: string;
    status: string;
    campaign_theme: string | null;
  }>;
}

function parseMetric(description: string | null, key: string): number | null {
  if (!description) return null;
  const re = new RegExp(`${key}[:\\s]+(\\d+)`, "i");
  const m = description.match(re);
  return m ? parseInt(m[1], 10) : null;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return iso;
  }
}

const TEAL = "#0F766E";

function LinkedInIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect width="16" height="16" rx="3" fill={TEAL} />
      <path d="M4 6.5h2V12H4V6.5zM5 5.5a1 1 0 110-2 1 1 0 010 2z" fill="#FFFFFF" />
      <path d="M7.5 6.5H9.3v.75h.02C9.57 6.84 10.1 6.3 11 6.3c2.1 0 2.5 1.38 2.5 3.17V12H11.5V9.9c0-.72-.01-1.65-1-1.65-1.01 0-1.17.79-1.17 1.6V12H7.5V6.5z" fill="#FFFFFF" />
    </svg>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {[1, 2].map((i) => (
        <div key={i} className="h-24 rounded bg-[hsl(var(--surface-2))]" />
      ))}
    </div>
  );
}

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  active:    { bg: "#DCFCE7", text: "#15803D" },
  planned:   { bg: "#FEF3C7", text: "#92400E" },
  completed: { bg: "#F5F5F4", text: "#6B7280" },
  paused:    { bg: "#FEE2E2", text: "#991B1B" },
};

export function LinkedInDripCard() {
  const session = getSession();
  const [campaigns, setCampaigns] = useState<LinkedInCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = session?.token;
    if (!token) { setError("Not authenticated"); setLoading(false); return; }

    // Use the year-range calendar endpoint and filter for linkedin type
    fetch("/api/dashboards/marketing/calendar?range=y", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
      .then((d: CalendarData) => {
        const linked = (d.events ?? [])
          .filter((e) =>
            (e.type ?? "").toLowerCase().includes("linkedin") ||
            (e.campaign_theme ?? "").toLowerCase().includes("linkedin") ||
            (e.name ?? "").toLowerCase().includes("linkedin")
          )
          .map((e) => ({
            id: e.id,
            name: e.name,
            event_date: e.event_date,
            status: e.status ?? "planned",
            campaign_theme: e.campaign_theme,
            description: e.description,
            reach: parseMetric(e.description, "reach"),
            engagement: parseMetric(e.description, "engagement"),
          }));
        setCampaigns(linked);
        setLoading(false);
      })
      .catch((e) => {
        console.error("[LinkedInDripCard]", e);
        setError("Could not load LinkedIn drip data");
        setLoading(false);
      });
  }, [session?.token]);

  if (loading) return <Skeleton />;
  if (error) return <p className="text-[12px] text-[#DC2626]">{error}</p>;

  if (campaigns.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-6 text-center">
        <div className="flex justify-center mb-3">
          <LinkedInIcon />
        </div>
        <p className="text-[13px] text-muted-foreground">
          No LinkedIn drip campaigns found for this year. Log LinkedIn events in the{" "}
          <a href="#/marketing/calendar" className="text-[#0F766E] hover:underline">
            marketing calendar
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {campaigns.map((c) => {
        const statusStyle = STATUS_STYLES[c.status?.toLowerCase()] ?? STATUS_STYLES.planned;
        return (
          <div
            key={c.id}
            className="rounded-lg border border-border bg-[hsl(var(--surface))] p-4"
            style={{ borderLeftWidth: 3, borderLeftColor: TEAL }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <LinkedInIcon />
                <span className="text-[13px] font-semibold text-foreground truncate">
                  {c.name}
                </span>
              </div>
              <span
                className="flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize"
                style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
              >
                {c.status}
              </span>
            </div>

            {c.campaign_theme && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Theme: {c.campaign_theme}
              </p>
            )}

            <div className="mt-3 flex flex-wrap gap-4">
              <div className="flex flex-col">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
                  Date
                </span>
                <span className="text-[12px] font-semibold tabular-nums text-foreground">
                  {formatDate(c.event_date)}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
                  Reach
                </span>
                <span className="text-[12px] font-semibold tabular-nums" style={{ color: TEAL }}>
                  {c.reach != null ? c.reach.toLocaleString() : "—"}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">
                  Engagement
                </span>
                <span className="text-[12px] font-semibold tabular-nums" style={{ color: TEAL }}>
                  {c.engagement != null ? c.engagement.toLocaleString() : "—"}
                </span>
              </div>
            </div>

            {c.description && (
              <p className="mt-2 text-[11px] text-muted-foreground leading-relaxed line-clamp-2">
                {c.description}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
